/**
 * Summary Tab Component
 * Main summary view that displays all summary metrics
 */

import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Container,
  Paper,
} from '@mui/material'
import { TrendingUp as TrendingUpIcon } from '@mui/icons-material'
import { useSummaryData } from '../../hooks/use-summary-data'
import { DashboardQueryParams } from '../../types'
import UserEngagementOverview from './user-engagement-overview'
import ActivityTypes from './activity-types'
import AssignmentContextComponent from './assignment-context'
import TopCategories from './top-categories'
import TopCourses from './top-courses'
import EventAttendanceComponent from './event-attendance'

interface SummaryTabProps {
  queryParams: DashboardQueryParams
}

export default function SummaryTab({ queryParams }: SummaryTabProps) {
  let { data, isLoading, error } = useSummaryData(queryParams)

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mx: 2 }}>
        Failed to load summary data. Please try again.
      </Alert>
    )
  }

  if (!data) {
    return null
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {/* Header */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          background:
            'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(33, 150, 243, 0.05) 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            <TrendingUpIcon />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              CE Activity Summary
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Comprehensive overview of continuing education progress and
              engagement
            </Typography>
          </Box>
        </Box>
      </Paper>
      {/* Main Content Grid */}
      <Box sx={{ display: 'grid', gap: 4 }}>
        {/* User Engagement Overview */}
        <UserEngagementOverview
          data={{
            ...data.userEngagement,
            totalCeHours: data.activityTypes.reduce(
              (sum, type) => sum + type.totalHours,
              0,
            ),
          }}
        />

        {/* Event Attendance */}
        <EventAttendanceComponent data={data.eventAttendance} />

        {/* Activity Types and Assignment Context */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
            gap: 4,
          }}
        >
          <ActivityTypes data={data.activityTypes} />
          <AssignmentContextComponent data={data.assignmentContext} />
        </Box>

        {/* Top Categories */}
        <TopCategories data={data.topCategories} />

        {/* Top Courses */}
        <TopCourses data={data.topCourses} />
      </Box>
    </Container>
  )
}
